<script setup lang="ts">
import { ref, watch } from 'vue'
import { usePWA } from '@/composables/usePWA'

const {
  canInstall,
  isOnline,
  needsRefresh,
  installApp,
  dismissInstallPrompt,
  updateApp,
} = usePWA()

const showInstallBanner = ref(false)
const showUpdateBanner = ref(false)
const installing = ref(false)

// Show install banner after a delay to avoid being intrusive
watch(canInstall, (value) => {
  if (value) {
    // Check if user has dismissed the banner recently
    const dismissed = localStorage.getItem('pwa-install-dismissed')
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10)
      const dayInMs = 24 * 60 * 60 * 1000
      if (Date.now() - dismissedAt < dayInMs * 7) {
        return // Don't show for 7 days after dismissal
      }
    }
    // Show banner after 30 seconds
    setTimeout(() => {
      if (canInstall.value) {
        showInstallBanner.value = true
      }
    }, 30000)
  }
}, { immediate: true })

// Show update banner when new version is available
watch(needsRefresh, (value) => {
  showUpdateBanner.value = value
})

async function handleInstall() {
  installing.value = true
  try {
    const installed = await installApp()
    if (installed) {
      showInstallBanner.value = false
    }
  } finally {
    installing.value = false
  }
}

function handleDismiss() {
  dismissInstallPrompt()
  showInstallBanner.value = false
  localStorage.setItem('pwa-install-dismissed', Date.now().toString())
}

function handleUpdate() {
  updateApp()
}

function handleDismissUpdate() {
  showUpdateBanner.value = false
}
</script>

<template>
  <!-- Offline indicator -->
  <Transition name="slide-down">
    <div
      v-if="!isOnline"
      class="fixed top-0 inset-x-0 z-50 bg-amber-600 text-white px-4 py-2 text-center text-sm font-medium"
      role="alert"
      aria-live="polite"
    >
      <span class="inline-flex items-center gap-2">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414" />
        </svg>
        You're offline. Some features may be unavailable.
      </span>
    </div>
  </Transition>

  <!-- Update available banner -->
  <Transition name="slide-up">
    <div
      v-if="showUpdateBanner"
      class="fixed bottom-0 inset-x-0 z-50 p-4 md:bottom-4 md:right-4 md:left-auto md:max-w-sm"
    >
      <div
        class="bg-ufc-dark border border-ufc-gold rounded-lg shadow-lg p-4"
        role="alert"
        aria-live="polite"
      >
        <div class="flex items-start gap-3">
          <div class="flex-shrink-0 p-2 bg-ufc-gold/20 rounded-full">
            <svg class="w-5 h-5 text-ufc-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <h3 class="text-white font-semibold text-sm">Update Available</h3>
            <p class="text-gray-400 text-xs mt-1">
              A new version of UFC Hub is available. Refresh to get the latest features.
            </p>
            <div class="flex gap-2 mt-3">
              <button
                type="button"
                class="btn btn-primary text-xs py-1.5 px-3"
                @click="handleUpdate"
              >
                Refresh Now
              </button>
              <button
                type="button"
                class="btn btn-outline text-xs py-1.5 px-3"
                @click="handleDismissUpdate"
              >
                Later
              </button>
            </div>
          </div>
          <button
            type="button"
            class="flex-shrink-0 p-1 text-gray-400 hover:text-white transition-colors focus-ring focus-ring-dark rounded"
            aria-label="Dismiss update notification"
            @click="handleDismissUpdate"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </Transition>

  <!-- Install prompt banner -->
  <Transition name="slide-up">
    <div
      v-if="showInstallBanner"
      class="fixed bottom-0 inset-x-0 z-50 p-4 md:bottom-4 md:right-4 md:left-auto md:max-w-sm"
    >
      <div
        class="bg-ufc-dark border border-gray-700 rounded-lg shadow-lg p-4"
        role="dialog"
        aria-labelledby="pwa-install-title"
        aria-describedby="pwa-install-desc"
      >
        <div class="flex items-start gap-3">
          <div class="flex-shrink-0">
            <img
              src="/icons/icon-72x72.png"
              alt=""
              class="w-12 h-12 rounded-lg"
              aria-hidden="true"
            />
          </div>
          <div class="flex-1 min-w-0">
            <h3 id="pwa-install-title" class="text-white font-semibold text-sm">
              Install UFC Hub
            </h3>
            <p id="pwa-install-desc" class="text-gray-400 text-xs mt-1">
              Add to your home screen for quick access and offline browsing.
            </p>
            <div class="flex gap-2 mt-3">
              <button
                type="button"
                class="btn btn-primary text-xs py-1.5 px-3"
                :disabled="installing"
                @click="handleInstall"
              >
                <span v-if="installing" class="inline-flex items-center gap-1">
                  <svg class="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Installing...
                </span>
                <span v-else>Install</span>
              </button>
              <button
                type="button"
                class="btn btn-outline text-xs py-1.5 px-3"
                @click="handleDismiss"
              >
                Not Now
              </button>
            </div>
          </div>
          <button
            type="button"
            class="flex-shrink-0 p-1 text-gray-400 hover:text-white transition-colors focus-ring focus-ring-dark rounded"
            aria-label="Dismiss install prompt"
            @click="handleDismiss"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .slide-down-enter-active,
  .slide-down-leave-active,
  .slide-up-enter-active,
  .slide-up-leave-active {
    transition: opacity 0.15s ease;
  }

  .slide-down-enter-from,
  .slide-down-leave-to,
  .slide-up-enter-from,
  .slide-up-leave-to {
    transform: none;
  }
}
</style>
